import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import SpecializationForm from "./SpecializationForm";
import SpecializationList from "./SpecializationList";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";

const SpecializationPage = () => {
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [specToDelete, setSpecToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchSpecializations();
  }, []);

  const fetchSpecializations = async () => {
    try {
      const res = await api.get("/admin-specializations");
      setSpecializations(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch specializations");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        const res = await api.put(`/admin-specializations/${editId}`, formData);
        setSpecializations(
          specializations.map((s) =>
            String(s.id) === String(editId) ? res.data.data : s
          )
        );
      } else {
        const res = await api.post("/admin-specializations", formData);
        setSpecializations((prev) => [...prev, res.data.data]);
      }
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save specialization");
    }
  };

  const handleDelete = async () => {
    if (!specToDelete) return;
    try {
      await api.delete(`/admin-specializations/${specToDelete}`);
      setSpecializations(specializations.filter((s) => s.id !== specToDelete));
      setSpecToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete specialization");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingSpec =
    action === "edit" && editId
      ? specializations.find((s) => String(s.id) === String(editId))
      : null;

  return (
    <>
      <div className="md:w-1/2">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Specializations</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Specialization
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <SpecializationForm
            specToEdit={editingSpec}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <SpecializationList
            specializations={specializations}
            onEdit={(spec) => setAction("edit", spec.id)}
            onDelete={(id) => setSpecToDelete(id)}
          />
        )}
      </div>

      {specToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this specialization? This action cannot be undone."
          onCancel={() => setSpecToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default SpecializationPage;
